import { ApiError } from '@discovr/common';
import type { ReportExportErrorCode } from '@discovr/types';

const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;
const HTTP_CONFLICT = 409;
const HTTP_GONE = 410;
const HTTP_TOO_MANY_REQUESTS = 429;
const HTTP_SERVICE_UNAVAILABLE = 503;
/** The status the shared client reports when no response arrived (offline, timeout). */
const NO_RESPONSE_STATUS = 0;

/** `error.details.reason` on a 429 when the user already has the maximum active exports. */
const EXPORT_USER_LIMIT_REASON = 'export_user_limit';
/** `error.details.reason` on a create 409 when the active export was built for the other viewer lane. */
const EXPORT_LANE_MISMATCH_REASON = 'export_lane_mismatch';
/** `error.details.reason` on a 403 when someone other than the requester tries to cancel. */
const EXPORT_CANCEL_NOT_OWNER_REASON = 'export_cancel_not_owner';

export const REPORT_EXPORT_ERROR_COPY = {
  userLimit: 'You already have 3 exports running. Wait for one to finish or cancel it, then try again.',
  tooMany: "You've exported a lot in the last few minutes. Try again shortly.",
  unavailable: 'Export is unavailable right now. Try again later.',
  notOwner: 'Only the person who started this export can cancel it.',
  gone: 'This export is no longer available.',
  laneMismatch: 'An export for another audience is already running on this campaign. Try again when it finishes.',
  queueBusy: 'Another export is already being prepared for this campaign. Try again in a moment.',
  finished: 'This export already finished.',
  notReady: 'Not ready yet.',
  invalidLink: "Couldn't open the download link. Try again.",
  timeout: 'The report took too long to build. Try again.',
  stalled: 'The export stopped unexpectedly. Try again.',
  sessionExpired: 'Your session has expired. Please sign in again.',
  forbidden: "You don't have permission to export this report.",
  notFound: 'This campaign is no longer available.',
  network: "Couldn't reach the server. Check your connection and try again.",
  generic: "Couldn't create the Excel file. Try again.",
} as const;

const COPY_BY_STATUS: Readonly<Record<number, string>> = {
  [HTTP_SERVICE_UNAVAILABLE]: REPORT_EXPORT_ERROR_COPY.unavailable,
  [HTTP_GONE]: REPORT_EXPORT_ERROR_COPY.gone,
  [HTTP_UNAUTHORIZED]: REPORT_EXPORT_ERROR_COPY.sessionExpired,
  [HTTP_FORBIDDEN]: REPORT_EXPORT_ERROR_COPY.forbidden,
  [HTTP_NOT_FOUND]: REPORT_EXPORT_ERROR_COPY.notFound,
  [NO_RESPONSE_STATUS]: REPORT_EXPORT_ERROR_COPY.network,
};

const COPY_BY_JOB_ERROR_CODE: Readonly<Record<string, string>> = {
  export_timeout: REPORT_EXPORT_ERROR_COPY.timeout,
  export_stalled: REPORT_EXPORT_ERROR_COPY.stalled,
  campaign_not_found: REPORT_EXPORT_ERROR_COPY.notFound,
  storage_unavailable: REPORT_EXPORT_ERROR_COPY.unavailable,
};

export type ReportExportOperation = 'create' | 'cancel' | 'download';

/** 409 means a different thing per call: cancelling a finished export, downloading one that is not done. */
const CONFLICT_COPY: Readonly<Record<ReportExportOperation, string>> = {
  create: REPORT_EXPORT_ERROR_COPY.queueBusy,
  cancel: REPORT_EXPORT_ERROR_COPY.finished,
  download: REPORT_EXPORT_ERROR_COPY.notReady,
};

function hasReason(details: unknown, reason: string): boolean {
  return typeof details === 'object' && details !== null && 'reason' in details && details.reason === reason;
}

function tooManyRequestsCopy(err: ApiError): string {
  return hasReason(err.details, EXPORT_USER_LIMIT_REASON) ? REPORT_EXPORT_ERROR_COPY.userLimit : REPORT_EXPORT_ERROR_COPY.tooMany;
}

function genericCopy(err: ApiError): string {
  return err.traceId ? `${REPORT_EXPORT_ERROR_COPY.generic} Reference: ${err.traceId}` : REPORT_EXPORT_ERROR_COPY.generic;
}

/** User-facing copy for a failed export request. The ApiError itself keeps the
 *  server's code and trace id for support. */
export function reportExportErrorMessage(err: unknown, operation: ReportExportOperation = 'create'): string {
  if (!(err instanceof ApiError)) return REPORT_EXPORT_ERROR_COPY.generic;
  if (err.httpStatus === HTTP_TOO_MANY_REQUESTS) return tooManyRequestsCopy(err);
  if (err.httpStatus === HTTP_FORBIDDEN && hasReason(err.details, EXPORT_CANCEL_NOT_OWNER_REASON)) {
    return REPORT_EXPORT_ERROR_COPY.notOwner;
  }
  if (err.httpStatus === HTTP_CONFLICT && operation === 'create' && hasReason(err.details, EXPORT_LANE_MISMATCH_REASON)) {
    return REPORT_EXPORT_ERROR_COPY.laneMismatch;
  }
  if (err.httpStatus === HTTP_CONFLICT) return CONFLICT_COPY[operation];
  return COPY_BY_STATUS[err.httpStatus] ?? genericCopy(err);
}

/** A 503 when starting an export means the kill switch is off or file storage is missing. List, cancel and download keep working. */
export function isReportExportUnavailable(err: unknown): boolean {
  return err instanceof ApiError && err.httpStatus === HTTP_SERVICE_UNAVAILABLE;
}

/** Copy for a job that finished as `failed`, keyed by the server's error code. */
export function reportExportJobErrorMessage(code: ReportExportErrorCode | null | undefined): string {
  return (code && COPY_BY_JOB_ERROR_CODE[code]) || REPORT_EXPORT_ERROR_COPY.generic;
}
